/** 投放项目真实 API；项目、章节共享内容及持久执行均由服务端管理。 */
import type {
  LandingPageDeliveryStatus,
  LandingProjectBookVisibilityWarningDto,
  LandingProjectListQuerySchema,
  LandingTaskAsset,
} from '@novel-admin/shared';
export type ProjectSort = ReturnType<typeof LandingProjectListQuerySchema.parse>['sort'];
export type ProjectDesensitizationFilter = NonNullable<
  ReturnType<typeof LandingProjectListQuerySchema.parse>['desensitization_status']
>;
import { apiRequest, apiRequestOffsetPaged } from '~/api/client';

export type {
  LandingProjectOperation as ProjectOperation,
  LandingProjectExecutionDto as ProjectProgress,
  LandingProjectQueueExecutionDto as ProjectQueueExecution,
  LandingProjectExecutionQueueState as ProjectQueueState,
  LandingProjectDto as ProjectSummary,
  LandingProjectDto as ProjectDetail,
  LandingProjectChapterDto as ProjectChapter,
  LandingProjectAssetDto as ProjectAsset,
  LandingProjectTaskDto as ProjectTask,
  LandingProjectChangeDto as ProjectChange,
  LandingProjectMutationDto as ProjectMutation,
  LandingChapterVersionDto as ProjectHistory,
  LandingChapterVersionDetailDto as ProjectHistoryDetail,
  CreateLandingProjectRequest as ProjectCreate,
  UpdateLandingProjectRequest as ProjectPatch,
} from '@novel-admin/shared';
import type {
  CopyLandingProjectRequest,
  LandingProjectOperation,
  LandingProjectAssetDto as ProjectAsset,
  LandingProjectChapterDto as ProjectChapter,
  CreateLandingProjectRequest as ProjectCreate,
  LandingProjectDto as ProjectDetail,
  LandingChapterVersionDto as ProjectHistory,
  LandingChapterVersionDetailDto as ProjectHistoryDetail,
  LandingProjectMutationDto as ProjectMutation,
  UpdateLandingProjectRequest as ProjectPatch,
  LandingProjectExecutionDto as ProjectProgress,
  LandingProjectQueueExecutionDto as ProjectQueueExecution,
  LandingProjectExecutionQueueState as ProjectQueueState,
  LandingProjectDto as ProjectSummary,
  LandingProjectTaskDto as ProjectTask,
} from '@novel-admin/shared';
const root = '/api/v1/landing-projects';
const path = (id: string, suffix = '') => `${root}/${encodeURIComponent(id)}${suffix}`;
export const landingProjectsApi = {
  bookVisibilityWarnings: (page: number) =>
    apiRequestOffsetPaged<LandingProjectBookVisibilityWarningDto>({
      method: 'GET',
      path: `${root}/book-visibility-warnings`,
      query: { page, limit: 20 },
    }),
  list: (
    page: number,
    limit: number,
    search: string,
    status?: string,
    sort: ProjectSort = 'updated_desc',
    productId?: string,
    language?: string,
    desensitizationStatus?: ProjectDesensitizationFilter,
  ) =>
    apiRequestOffsetPaged<ProjectSummary>({
      method: 'GET',
      path: root,
      query: {
        page,
        limit,
        search: search || undefined,
        status,
        sort,
        product_id: productId,
        language,
        desensitization_status: desensitizationStatus,
      },
    }),
  detail: (id: string) => apiRequest<ProjectDetail>({ method: 'GET', path: path(id) }),
  create: (body: ProjectCreate) =>
    apiRequest<ProjectMutation>({ method: 'POST', path: root, body }),
  update: (id: string, body: ProjectPatch) =>
    apiRequest<ProjectMutation>({ method: 'PATCH', path: path(id), body }),
  copy: (id: string, body: CopyLandingProjectRequest) =>
    apiRequest<ProjectMutation>({ method: 'POST', path: path(id, '/copy'), body }),
  availableChapters: (
    id: string,
    page: number,
    limit: number,
    search: string,
    availability: 'free' | 'paid' | 'all' = 'all',
  ) =>
    apiRequestOffsetPaged<ProjectChapter>({
      method: 'GET',
      path: path(id, '/available-chapters'),
      query: { page, limit, search: search || undefined, availability },
    }),
  chapters: (id: string) =>
    apiRequest<ProjectChapter[]>({ method: 'GET', path: path(id, '/chapters') }),
  chapterContent: (id: string, chapterId: string) =>
    apiRequest<import('@novel-admin/shared').LandingProjectChapterContentDto>({
      method: 'GET',
      path: path(id, `/chapters/${encodeURIComponent(chapterId)}/content`),
    }),
  manualContent: (
    id: string,
    chapterId: string,
    body: {
      content: string;
      expected_content_version_id: string | null;
      expected_source_sha256: string;
    },
  ) =>
    apiRequest<ProjectMutation>({
      method: 'POST',
      path: path(id, `/chapters/${encodeURIComponent(chapterId)}/manual-content`),
      body,
    }),
  replaceChapters: (id: string, chapter_ids: string[]) =>
    apiRequest<ProjectMutation>({
      method: 'PUT',
      path: path(id, '/chapters'),
      body: { chapter_ids },
    }),
  process: (
    id: string,
    body: { operation: 'review' | 'desensitize'; chapter_ids?: string[]; force?: boolean },
  ) =>
    apiRequest<ProjectProgress>({
      method: 'POST',
      path: path(id, '/content-executions'),
      body,
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    }),
  assets: (id: string, page: number, limit: number) =>
    apiRequestOffsetPaged<ProjectAsset>({
      method: 'GET',
      path: path(id, '/assets'),
      query: { page, limit },
    }),
  addAsset: (
    id: string,
    body: {
      name: string;
      asset: LandingTaskAsset;
      width?: number;
      height?: number;
      bytes?: number;
      content_type?: string;
    },
    idempotencyKey: string = crypto.randomUUID(),
  ) =>
    apiRequest<ProjectMutation>({
      method: 'POST',
      path: path(id, '/assets'),
      body,
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
  updateAsset: (id: string, assetId: string, body: { name?: string; asset?: LandingTaskAsset }) =>
    apiRequest<ProjectMutation>({
      method: 'PATCH',
      path: path(id, `/assets/${encodeURIComponent(assetId)}`),
      body,
    }),
  removeAsset: (id: string, assetId: string) =>
    apiRequest<ProjectMutation>({
      method: 'DELETE',
      path: path(id, `/assets/${encodeURIComponent(assetId)}`),
    }),
  tasks: (id: string, page: number, limit: number) =>
    apiRequestOffsetPaged<ProjectTask>({
      method: 'GET',
      path: path(id, '/tasks'),
      query: { page, limit },
    }),
  /** 投放标记只记运营在广告平台的使用情况，不触发发布（AE §11.4.1）。 */
  setTaskDelivery: (id: string, taskId: string, deliveryStatus: LandingPageDeliveryStatus) =>
    apiRequest<ProjectTask>({
      method: 'PUT',
      path: path(id, `/tasks/${encodeURIComponent(taskId)}/delivery`),
      body: { delivery_status: deliveryStatus },
    }),
  execute: (
    id: string,
    body: { operation: 'generate' | 'publish'; task_ids?: string[]; retry_failed_only?: boolean },
  ) =>
    apiRequest<ProjectProgress>({
      method: 'POST',
      path: path(id, '/executions'),
      body,
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    }),
  executions: (id: string, page: number, limit: number) =>
    apiRequestOffsetPaged<ProjectProgress>({
      method: 'GET',
      path: path(id, '/executions'),
      query: { page, limit },
    }),
  /** 跨项目执行队列总览（AE §11.5.1）；executions 是保留字面量路径段，不是项目 ID。 */
  executionQueue: (
    page: number,
    limit: number,
    state: ProjectQueueState,
    operation?: LandingProjectOperation,
  ) =>
    apiRequestOffsetPaged<ProjectQueueExecution>({
      method: 'GET',
      path: `${root}/executions`,
      query: { page, limit, state, operation },
    }),
  execution: (id: string, executionId: string) =>
    apiRequest<ProjectProgress>({
      method: 'GET',
      path: path(id, `/executions/${encodeURIComponent(executionId)}`),
    }),
  retry: (id: string, executionId: string) =>
    apiRequest<ProjectProgress>({
      method: 'POST',
      path: path(id, `/executions/${encodeURIComponent(executionId)}/retry`),
      body: {},
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    }),
  cancel: (id: string, executionId: string) =>
    apiRequest<ProjectProgress>({
      method: 'POST',
      path: path(id, `/executions/${encodeURIComponent(executionId)}/cancel`),
      body: {},
    }),
  history: (id: string, chapterId: string, page: number, limit: number) =>
    apiRequestOffsetPaged<ProjectHistory>({
      method: 'GET',
      path: path(id, `/chapters/${encodeURIComponent(chapterId)}/history`),
      query: { page, limit },
    }),
  historyDetail: (id: string, chapterId: string, versionId: string) =>
    apiRequest<ProjectHistoryDetail>({
      method: 'GET',
      path: path(
        id,
        `/chapters/${encodeURIComponent(chapterId)}/history/${encodeURIComponent(versionId)}`,
      ),
    }),
  adopt: (
    id: string,
    chapterId: string,
    content_version_id: string,
    confirm_content_reviewed = false,
  ) =>
    apiRequest<ProjectMutation>({
      method: 'POST',
      path: path(id, `/chapters/${encodeURIComponent(chapterId)}/adopt`),
      body: {
        content_version_id,
        ...(confirm_content_reviewed ? { confirm_content_reviewed: true } : {}),
      },
    }),
  reject: (id: string, chapterId: string, expected_content_version_id: string, reason: string) =>
    apiRequest<ProjectMutation>({
      method: 'POST',
      path: path(id, `/chapters/${encodeURIComponent(chapterId)}/reject`),
      body: { expected_content_version_id, reason },
    }),
};
